import type { ChangeEvent } from 'react';
import { useAuthedImage } from '../../lib/hooks';

/**
 * The two things the CS pages need that the shared components do not cover:
 * showing a logo that sits behind the bearer token, and picking one to upload.
 */

/** A logo if there is one, the first letters of the name if there is not. */
export function LogoTile({ path, name }: { path: string | null; name: string }) {
  const url = useAuthedImage(path);
  if (url) return <img className="cs-logo" src={url} alt="" />;
  return (
    <span className="cs-logo cs-logo-fallback" aria-hidden="true">
      {name
        .replace(/\s*\([^)]*\)\s*/g, ' ')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0] ?? '')
        .join('')
        .toUpperCase()}
    </span>
  );
}

export const LOGO_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico'];
export const MAX_LOGO_MB = 2;

/**
 * Reads a chosen image as base64 for the JSON upload body.
 *
 * Base64 on JSON rather than multipart, matching how a job description is
 * uploaded — one request shape for the whole API is worth more than the ~33%
 * the encoding costs on a file capped at 2 MB.
 */
export function LogoPicker({
  onPicked,
  onError,
  label = 'Upload a logo',
}: {
  onPicked: (file: { fileName: string; content: string }) => void;
  onError: (message: string) => void;
  label?: string;
}) {
  const handle = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    /* Clear immediately, so picking the same file twice in a row still fires. */
    event.target.value = '';
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!LOGO_EXTENSIONS.includes(ext)) {
      onError(`Logos must be one of: ${LOGO_EXTENSIONS.map((e) => `.${e}`).join(', ')}`);
      return;
    }
    if (file.size > MAX_LOGO_MB * 1024 * 1024) {
      onError(`That image is larger than ${MAX_LOGO_MB} MB.`);
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => onError('That image could not be read.');
    reader.onload = () => {
      const result = String(reader.result ?? '');
      /* Strip the data: prefix — the API wants bare base64. */
      const content = result.slice(result.indexOf(',') + 1);
      if (!content) {
        onError('That image could not be read.');
        return;
      }
      onPicked({ fileName: file.name, content });
    };
    reader.readAsDataURL(file);
  };

  return (
    <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
      {label}
      <input
        type="file"
        accept={LOGO_EXTENSIONS.map((e) => `.${e}`).join(',')}
        onChange={handle}
        style={{ display: 'none' }}
      />
    </label>
  );
}

/** A link that only renders when there is somewhere to go. */
export function PortalLink({ href, children }: { href: string | null; children: string }) {
  if (!href) return null;
  return (
    <a className="cs-portal" href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}
